import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  VStack,
  Text,
  Card,
  CardBody,
  useColorModeValue,
  SimpleGrid,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from 'react-table';
import { maskCurrency } from 'utils/number';
import MiniStatistics from './card/MiniStatistics';
import { splitMobileCardFields } from './mobileCardFields';

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }: any, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef = ref || (defaultRef as any);

  React.useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Checkbox ref={resolvedRef} {...rest} />;
});

export default function MobileList(props: {
  columnsData: any[];
  itensData: any[];
  actions: any;
  pageCount?: number;
  setFilter: any;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: { id: string; desc?: boolean }[];
  enableSelect?: boolean;
  setSelectedRows?: any;
  aggregateSelection?: {
    key: string;
    label: string;
  }[];
}) {
  const {
    columnsData,
    itensData,
    actions,
    pageCount,
    setFilter,
    pageIndex: controlledPageIndex = 0,
    pageSize: controlledPageSize = 10,
    orderBy: controlledOrderBy = [],
    enableSelect,
    setSelectedRows,
    aggregateSelection,
  } = props;

  const columns = useMemo(() => columnsData, [columnsData]);
  const tableData = useMemo(() => itensData, [itensData]);

  const tableInstance = useTable(
    {
      columns,
      manualPagination: true,
      manualSortBy: true,
      initialState: {
        pageIndex: controlledPageIndex,
        pageSize: controlledPageSize,
        sortBy: controlledOrderBy,
      },
      data: tableData || [],
      autoResetPage: false,
      pageCount: pageCount || 0,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      enableSelect &&
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }: any) => (
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} isChecked={row.isSelected} />
            ),
          },
          ...columns,
        ]);
    },
  );

  const {
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy, selectedRowIds },
  } = tableInstance;

  const prevControlledPageIndex = useRef(controlledPageIndex);
  const prevControlledPageSize = useRef(controlledPageSize);

  useEffect(() => {
    if (prevControlledPageIndex.current !== controlledPageIndex) {
      prevControlledPageIndex.current = controlledPageIndex;
      if (pageIndex !== controlledPageIndex) {
        gotoPage(controlledPageIndex);
      }
    }
  }, [controlledPageIndex, gotoPage, pageIndex]);

  useEffect(() => {
    if (prevControlledPageSize.current !== controlledPageSize) {
      prevControlledPageSize.current = controlledPageSize;
      if (pageSize !== controlledPageSize) {
        setPageSize(controlledPageSize);
      }
    }
  }, [controlledPageSize, setPageSize, pageSize]);

  useEffect(() => {
    setFilter((prev: any) => {
      const prevOrderBy = JSON.stringify(prev.orderBy ?? []);
      const nextOrderBy = JSON.stringify(sortBy ?? []);
      if (
        prev.pageIndex === pageIndex &&
        prev.pageSize === pageSize &&
        prevOrderBy === nextOrderBy
      ) {
        return prev;
      }
      prevControlledPageIndex.current = pageIndex;
      prevControlledPageSize.current = pageSize;
      return { ...prev, pageIndex, pageSize, orderBy: sortBy };
    });
  }, [pageIndex, pageSize, sortBy, setFilter]);

  useEffect(() => {
    setSelectedRows &&
      setSelectedRows(
        tableData
          .map((item: any, i) => {
            if (selectedRowIds[i]) {
              return item.id;
            }
            return null;
          })
          .filter((item: any) => item !== null),
      );
  }, [selectedRowIds, setSelectedRows, tableData]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.700');

  // Campos extras abertos por linha. Zera quando a página de dados troca, para a
  // linha nova não herdar o "Mais detalhes" da linha que estava na mesma posição.
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setExpandedRows({});
  }, [tableData]);

  return (
    <Box>
      {/* Aggregate Statistics */}
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={4}>
        {aggregateSelection?.map(({ key, label }) => (
          <MiniStatistics
            key={key}
            name={label}
            value={maskCurrency(
              tableData
                .map((item: any, i) => (selectedRowIds[i] ? item : null))
                .filter((item: any) => item !== null)
                .reduce((acc, cur) => acc + Math.abs(+cur[key]), 0) || 0,
            )}
          />
        ))}
      </SimpleGrid>

      {/* Empty State */}
      {tableData?.length === 0 && (
        <Flex justify="center" align="center" h="200px" color="gray.500">
          <Text>Nenhum registro encontrado</Text>
        </Flex>
      )}

      {/* Card List */}
      <VStack spacing={4} mb={4}>
        {page.map((row, idx) => {
          prepareRow(row);
          // `actions` do card vem da prop (não da célula), como já era antes.
          const { selection, title, visible, hidden } = splitMobileCardFields(row.cells);
          const rowKey = (row.original as any)?.id ?? idx;
          const isExpanded = !!expandedRows[rowKey];

          return (
            <Card key={idx} w="full" bg={cardBg} boxShadow="sm" borderRadius="lg">
              <CardBody px={4} py={3}>
                <VStack align="stretch" spacing={2}>
                  {/*
                    Cabeçalho do card: seleção, campo de destaque e as ações da
                    linha. As ações ficam aqui, e não no fim, para que o botão de
                    acessar o registro esteja visível sem rolar o card inteiro.
                  */}
                  <Flex align="flex-start" justify="space-between" gap={3}>
                    <HStack spacing={2} align="flex-start" minW={0} flex="1">
                      {enableSelect &&
                        selection.map((cell, cellIdx) => (
                          <Box key={cellIdx} pt={1}>
                            {cell.render('Cell') as React.ReactNode}
                          </Box>
                        ))}
                      {title && (
                        <Box minW={0}>
                          <Text color="gray.500" fontSize="xs">
                            {title.column.Header as string}
                          </Text>
                          <Box color={textColor} fontSize="sm" fontWeight="700">
                            {title.render('Cell') as React.ReactNode}
                          </Box>
                        </Box>
                      )}
                    </HStack>
                    {actions && (
                      <HStack spacing={1} flexShrink={0}>
                        {actions(row.original)}
                      </HStack>
                    )}
                  </Flex>

                  {(isExpanded ? [...visible, ...hidden] : visible).map((cell, cellIdx) => (
                    <Flex key={cellIdx} justify="space-between" gap={3}>
                      <Text color="gray.500" fontSize="sm" flexShrink={0}>
                        {cell.column.Header as string}:
                      </Text>
                      <Box color={textColor} fontSize="sm" fontWeight="500" textAlign="right">
                        {cell.render('Cell') as React.ReactNode}
                      </Box>
                    </Flex>
                  ))}

                  {hidden.length > 0 && (
                    <Button
                      size="xs"
                      variant="link"
                      alignSelf="flex-start"
                      colorScheme="brand"
                      onClick={() =>
                        setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                      }
                    >
                      {isExpanded ? 'Menos detalhes' : `Mais detalhes (${hidden.length})`}
                    </Button>
                  )}
                </VStack>
              </CardBody>
            </Card>
          );
        })}
      </VStack>

      {/* Pagination */}
      <Flex justify="center" direction="column" align="center" w="full">
        <HStack spacing={2} mb={4}>
          <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} size="sm" variant="outline">
            {'<<'}
          </Button>
          <Button onClick={() => previousPage()} disabled={!canPreviousPage} size="sm" variant="outline">
            {'<'}
          </Button>
          <Button onClick={() => nextPage()} disabled={!canNextPage} size="sm" variant="outline">
            {'>'}
          </Button>
          <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} size="sm" variant="outline">
            {'>>'}
          </Button>
        </HStack>

        <VStack spacing={2}>
          <Text fontSize="sm">
            Página {pageIndex + 1} de {pageOptions.length}
          </Text>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ padding: '4px 8px', borderRadius: '4px' }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                Exibir {size}
              </option>
            ))}
          </select>
        </VStack>
      </Flex>
    </Box>
  );
}
